import { PageData, PageListItem } from './types';
import { handleApiResponse } from '../common';
import { getApiBaseUrl, API_VERSION } from '../index';

/**
 * Pages API client
 * Follows the same patterns as spacesApi
 */
export const pagesApi = {
  /**
   * List all pages
   * @param {boolean} publishedOnly - Only return published pages
   * @returns {Promise<PageListItem[]>}
   */
  async list(publishedOnly = false) {
    try {
      const url = `${getApiBaseUrl()}${API_VERSION}/pages?published_only=${publishedOnly}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();

      if (!Array.isArray(data)) {
        console.error('Unexpected data format:', data);
        throw new Error('Invalid data format received from API');
      }

      return PageListItem.fromApiResponseList(data);
    } catch (error) {
      console.error('Failed to fetch pages:', error);
      throw error;
    }
  },

  /**
   * Get a single page by ID
   * @param {number} pageId
   * @returns {Promise<PageData>}
   */
  async get(pageId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/pages/${pageId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return PageData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to fetch page ${pageId}:`, error);
      throw error;
    }
  },

  /**
   * Get a single page by slug
   * @param {string} slug
   * @returns {Promise<PageData>}
   */
  async getBySlug(slug) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/pages/slug/${slug}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return PageData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to fetch page by slug ${slug}:`, error);
      throw error;
    }
  },

  /**
   * Create a new page
   * @param {Object} pageData - Page creation data
   * @returns {Promise<PageData>}
   */
  async create(pageData) {
    try {
      const payload = pageData instanceof PageData 
        ? pageData.toCreateFormat() 
        : pageData;

      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/pages`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return PageData.fromApiResponse(data);
    } catch (error) {
      console.error('Failed to create page:', error);
      throw error;
    }
  },

  /**
   * Update an existing page
   * @param {number} pageId
   * @param {Object} updates - Partial page data to update
   * @returns {Promise<PageData>}
   */
  async update(pageId, updates) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/pages/${pageId}`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return PageData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to update page ${pageId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a page
   * @param {number} pageId
   * @returns {Promise<{message: string}>}
   */
  async delete(pageId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/pages/${pageId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      return await validatedResponse.json();
    } catch (error) {
      console.error(`Failed to delete page ${pageId}:`, error);
      throw error;
    }
  },

  /**
   * Publish a page
   * @param {number} pageId
   * @returns {Promise<PageData>}
   */
  async publish(pageId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/pages/${pageId}/publish`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return PageData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to publish page ${pageId}:`, error);
      throw error;
    }
  },

  /**
   * Unpublish a page
   * @param {number} pageId
   * @returns {Promise<PageData>}
   */
  async unpublish(pageId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/pages/${pageId}/unpublish`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return PageData.fromApiResponse(data);
    } catch (error) {
      console.error(`Failed to unpublish page ${pageId}:`, error);
      throw error;
    }
  }
};









